import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { MessageCircle, X, Send, CheckCircle2, AlertCircle, Phone } from "lucide-react";
import { BUSINESS } from "@/data/site";
import { submitNetlify, submitBackend } from "@/lib/submit";

const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

const inputCls =
  "w-full rounded-xl border border-sand bg-ivory px-4 py-3 text-base text-charcoal placeholder:text-warmgray/60 focus:border-gold focus:ring-2 focus:ring-gold/30 outline-none transition";

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", contact: "", message: "", company: "" });
  const reduce = useReducedMotion();
  const closeRef = useRef(null);

  useEffect(() => {
    if (open) closeRef.current?.focus();
  }, [open]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (form.name.trim().length < 2 || form.contact.trim().length < 5 || form.message.trim().length < 5) {
      setError("Please complete all fields so we can reach you.");
      return;
    }
    setError("");
    setStatus("sending");
    const isEmail = EMAIL_RE.test(form.contact.trim());
    const payload = {
      name: form.name.trim(),
      email: isEmail ? form.contact.trim() : "",
      phone: isEmail ? "" : form.contact.trim(),
      about: "Myself",
      roomPreference: "Not Sure",
      moveIn: "Just Researching",
      message: form.message.trim(),
      topic: "chat",
      company: form.company,
    };
    const viaNetlify = await submitNetlify("chat", { ...form });
    const viaBackend = await submitBackend(payload);
    if (viaNetlify || viaBackend) {
      setStatus("success");
    } else {
      setStatus("error");
      setError("We couldn’t send your message. Please call us instead.");
    }
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            role="dialog"
            aria-label="Chat with Savannah's House"
            initial={reduce ? false : { opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={reduce ? undefined : { opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-4 sm:right-6 z-[60] w-[calc(100vw-2rem)] max-w-sm bg-white rounded-3xl border border-sand/50 shadow-lift overflow-hidden"
            data-testid="chat-panel"
          >
            <div className="bg-espresso text-ivory p-5 flex items-start justify-between gap-3">
              <div>
                <p className="font-serif text-xl font-semibold">Chat with Savannah’s House</p>
                <p className="mt-1 text-xs text-ivory/70 leading-relaxed">
                  We reply during business hours — {BUSINESS.hours}.
                </p>
              </div>
              <button
                ref={closeRef}
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close chat"
                data-testid="chat-close-button"
                className="p-1.5 rounded-full hover:bg-ivory/10 transition-colors shrink-0"
              >
                <X size={20} aria-hidden="true" />
              </button>
            </div>

            <div className="p-5">
              {status === "success" ? (
                <div className="text-center py-6" role="status" data-testid="chat-success-message">
                  <CheckCircle2 size={40} className="mx-auto text-sage" aria-hidden="true" />
                  <p className="mt-3 font-serif text-xl font-semibold text-espresso">Message Sent</p>
                  <p className="mt-2 text-sm text-warmgray leading-relaxed">
                    Thank you — we’ll be in touch soon. Need us sooner? Call{" "}
                    <a href={BUSINESS.phoneHref} className="text-espresso font-semibold underline decoration-gold underline-offset-4">
                      {BUSINESS.phone}
                    </a>
                    .
                  </p>
                </div>
              ) : (
                <form onSubmit={onSubmit} noValidate className="space-y-3.5" data-testid="chat-form">
                  <div>
                    <label htmlFor="chat-name" className="block text-sm font-semibold text-espresso mb-1">
                      Your Name
                    </label>
                    <input id="chat-name" type="text" autoComplete="name" className={inputCls} value={form.name} onChange={update("name")} data-testid="chat-name-input" required />
                  </div>
                  <div>
                    <label htmlFor="chat-contact" className="block text-sm font-semibold text-espresso mb-1">
                      Email or Phone
                    </label>
                    <input id="chat-contact" type="text" autoComplete="email" className={inputCls} value={form.contact} onChange={update("contact")} data-testid="chat-contact-input" required />
                  </div>
                  <div>
                    <label htmlFor="chat-message" className="block text-sm font-semibold text-espresso mb-1">
                      How can we help?
                    </label>
                    <textarea id="chat-message" rows={3} className={inputCls} value={form.message} onChange={update("message")} data-testid="chat-message-input" required />
                  </div>
                  <div className="hidden" aria-hidden="true">
                    <label htmlFor="chat-company">Company</label>
                    <input id="chat-company" type="text" tabIndex={-1} autoComplete="off" value={form.company} onChange={update("company")} />
                  </div>
                  {error && (
                    <p role="alert" className="text-sm text-terra font-medium flex items-center gap-1.5" data-testid="chat-error-message">
                      <AlertCircle size={14} aria-hidden="true" />
                      {error}
                    </p>
                  )}
                  <button
                    type="submit"
                    disabled={status === "sending"}
                    data-testid="chat-submit-button"
                    className="w-full inline-flex items-center justify-center gap-2 bg-espresso hover:bg-charcoal disabled:opacity-60 text-ivory font-semibold px-6 py-3.5 rounded-full transition-colors"
                  >
                    <Send size={16} aria-hidden="true" />
                    {status === "sending" ? "Sending…" : "Send Message"}
                  </button>
                  <a
                    href={BUSINESS.phoneHref}
                    data-testid="chat-call-link"
                    className="flex items-center justify-center gap-2 text-sm font-semibold text-espresso hover:text-gold transition-colors pt-1"
                  >
                    <Phone size={15} aria-hidden="true" />
                    Prefer to call? {BUSINESS.phone}
                  </a>
                </form>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-label={open ? "Close chat with Savannah's House" : "Chat with Savannah's House"}
        data-testid="chat-open-button"
        whileHover={reduce ? undefined : { scale: 1.08 }}
        whileTap={reduce ? undefined : { scale: 0.94 }}
        className="fixed bottom-5 right-5 z-[60] w-14 h-14 rounded-full bg-espresso text-ivory shadow-lift hover:bg-charcoal flex items-center justify-center transition-colors"
      >
        {open ? <X size={24} aria-hidden="true" /> : <MessageCircle size={24} aria-hidden="true" />}
      </motion.button>
    </>
  );
};

export default ChatWidget;
